import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

const LogEntry: React.FC<{ log: any }> = ({ log }) => {
  try {
    return (
      <div
        className={`text-sm p-2 rounded ${
          log.type === 'error'
            ? 'bg-red-100 text-red-800'
            : log.type === 'warn'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-gray-100'
        }`}
      >
        <div className="text-xs text-gray-500">
          {new Date(log.timestamp).toLocaleTimeString()}
        </div>
        <div className="font-mono whitespace-pre-wrap">
          {typeof log.data === 'string' 
            ? log.data 
            : JSON.stringify(log.data, null, 2)}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {log.url}
        </div>
      </div>
    );
  } catch (e) {
    return (
      <div className="text-sm p-2 rounded bg-red-100 text-red-800">
        Failed to render log entry
      </div>
    );
  }
};

export const DebugPanel: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Update logs every second
      const interval = setInterval(() => {
        try {
          const newLogs = logger.getLogs();
          setLogs(newLogs);
          setError(null);
        } catch (e) {
          setError('Failed to get logs');
          console.error('Failed to get logs:', e);
        }
      }, 1000);

      return () => clearInterval(interval);
    } catch (e) {
      setError('Failed to initialize debug panel');
      console.error('Failed to initialize debug panel:', e);
    }
  }, []);

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      >
        Show Debug Panel
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 h-96 bg-white rounded-lg shadow-lg z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold">Debug Panel</h3>
        <div className="space-x-2">
          <button
            onClick={() => {
              try {
                logger.clearLogs();
                setLogs([]);
                setError(null);
              } catch (e) {
                setError('Failed to clear logs');
                console.error('Failed to clear logs:', e);
              }
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto p-4 space-y-2">
        {error && (
          <div className="text-sm p-2 rounded bg-red-100 text-red-800">
            {error}
          </div>
        )}
        {logs.map((log, index) => (
          <LogEntry key={index} log={log} />
        ))}
      </div>
    </div>
  );
}; 